import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { AiBotIcon } from '../components/icons/AppIcon';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, ConfirmationModal } from '../components';
import { CatalogueList } from '../components/catalogues';
import { ChannelBindingModal } from '../components/channels';
import { ProductSelectorModal } from '../components/catalogues/ProductSelectorModal';
import { countActive, FilterField, fv, QuickView, relativeRange, SortOption, useListFilters } from '../components/listFilters/listFilters';
import { FilterSheet } from '../components/listFilters/FilterSheet';
import { ListFilterBar } from '../components/listFilters/ListFilterBar';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

// Category fields filterable by their stored values ("More fields" in the filter
// sheet, next to custom fields) — matches CATALOGUE_FIXED_FIELD_PATHS server-side.
const CATALOGUE_VALUE_FIELDS = ['name', 'currency', 'locale'];

interface CataloguesListScreenProps {
  /** Set when this screen is reached via a system Catalog Data List's scoped
   * mode (see DataListRecordsScreen.tsx) — restricts the list to only
   * records added THROUGH that list, and shows an exit banner back to it. */
  scopedToDataListId?: string;
  scopedListName?: string;
}

export const CataloguesListScreen: React.FC<CataloguesListScreenProps> = ({ scopedToDataListId, scopedListName }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const { currentBusiness } = useAppStore();
  const { t } = useLocale();
  const [catalogues, setCatalogues] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [catalogueToDelete, setCatalogueToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showChannelBindingModal, setShowChannelBindingModal] = useState(false);
  const [selectedCatalogueId, setSelectedCatalogueId] = useState<string | null>(null);
  const [availableChannels, setAvailableChannels] = useState<any[]>([]);
  const [channelBindings, setChannelBindings] = useState<any[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);

  // Filters — the shared list filters: remembered per screen, quick views + saved views.
  const quickViews = useMemo<QuickView[]>(() => [
    { key: 'empty', label: t('catalogues.view.empty'), icon: 'file-tray-outline', filters: { products: { max: 0 } } },
    { key: 'new', label: t('catalogues.view.new'), icon: 'sparkles-outline', filters: { createdAt: relativeRange.days(30) } },
    // The AI's on-the-fly catalogues (one conversation, removed after its workflow): hidden unless asked for.
    { key: 'ai', label: t('catalogues.temporaryFilter'), icon: 'sparkles', filters: { temporary: 'only' } },
  ], [t]);
  const list = useListFilters(scopedToDataListId ? `catalogues:${scopedToDataListId}` : 'catalogues', { quickViews });
  const [showFilters, setShowFilters] = useState(false);
  const catalogueFields = useMemo<FilterField[]>(() => [
    { key: 'products', label: t('catalogues.filter.products'), type: 'range' },
    { key: 'createdAt', label: t('catalogues.filter.added'), type: 'date' },
    { key: 'temporary', label: t('catalogues.filter.kind'), type: 'choice', options: [{ value: 'only', label: t('catalogues.temporaryFilter') }] },
  ], [t]);
  const catalogueSorts = useMemo<SortOption[]>(() => [
    { key: 'newest', label: t('catalogues.sort.newest'), sortBy: 'createdAt', sortOrder: 'desc', icon: 'arrow-down-outline' },
    { key: 'name', label: t('catalogues.sort.name'), sortBy: 'name', sortOrder: 'asc', icon: 'text-outline' },
    { key: 'products', label: t('catalogues.sort.products'), sortBy: 'productCount', sortOrder: 'desc', icon: 'cube-outline' },
    { key: 'updated', label: t('catalogues.sort.updated'), sortBy: 'updatedAt', sortOrder: 'desc', icon: 'time-outline' },
  ], [t]);
  const activeFilterCount = countActive(catalogueFields, list.state);

  const [customFieldKeys, setCustomFieldKeys] = useState<string[]>([]);
  useEffect(() => {
    apiService.getCatalogueCustomFieldKeys().then((res) => {
      if (res.statusCode === 1000 && res.data) setCustomFieldKeys(res.data);
    }).catch(() => {});
  }, []);
  const customFieldsConfig = useMemo(() => ({
    fields: [
      ...CATALOGUE_VALUE_FIELDS.map((key) => ({ key, label: t(`catalogues.field.${key}` as any) })),
      { key: 'hasProducts', label: t('catalogues.field.hasProducts'), hasValueOnly: true },
      ...customFieldKeys.map((key) => ({ key, label: key })),
    ],
    loadValues: async (key: string) => (await apiService.getCatalogueCustomFieldValues(key)).data || [],
  }), [customFieldKeys, t]);

  const loadCatalogues = useCallback(async (page: number = 0, reset: boolean = false) => {
    try {
      if (reset) {
        setLoading(true);
        setError(null);
      }

      const response = await apiService.getCatalogues({
        limit: 20,
        skip: page * 20,
        ...(() => {
          const f = list.state.filters;
          const sort = catalogueSorts.find((x) => x.key === list.state.sort) || catalogueSorts[0];
          return {
            search: fv.text(list.state.search),
            sortBy: sort.sortBy as any,
            sortOrder: sort.sortOrder,
            minProducts: fv.min(f.products),
            maxProducts: fv.max(f.products),
            createdFrom: f.createdAt?.from ? `${f.createdAt.from}T00:00:00.000` : undefined,
            createdTo: f.createdAt?.to ? `${f.createdAt.to}T23:59:59.999` : undefined,
            customFieldFilters: fv.customFields(list.state.customFields),
            ...(f.temporary === 'only' ? { temporary: 'only' as const } : {}),
          };
        })(),
        dataListId: scopedToDataListId,
      });

      if (response.statusCode === 1000 && response.data) {
        const newCatalogues = response.data.catalogues || [];
        setCatalogues((prev) => (reset ? newCatalogues : [...prev, ...newCatalogues]));
        setTotal(response.data.pagination?.total || 0);
        setHasMore(newCatalogues.length === 20);
        setCurrentPage(page);
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to load catalogues';
        setError(errorMessage);
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error loading catalogues:', error);
      const errorMessage = error.message || 'Failed to load catalogues';
      setError(errorMessage);
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showNotification, scopedToDataListId, list.state, catalogueSorts]);

  useEffect(() => {
    if (!list.ready) return;
    const timer = setTimeout(() => loadCatalogues(0, true), list.state.search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [loadCatalogues, list.ready]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadCatalogues(0, true);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore && !error) {
      loadCatalogues(currentPage + 1, false);
    }
  };

  const handleCataloguePress = (catalogueId: string) => {
    navigateTo('catalogue-detail', catalogueId);
  };

  const handleCatalogueEdit = (catalogueId: string) => {
    navigateTo('catalogue-edit', catalogueId, scopedToDataListId ? { listId: scopedToDataListId } : undefined);
  };

  const handleCatalogueDelete = (catalogueId: string) => {
    setCatalogueToDelete(catalogueId);
    setShowDeleteModal(true);
  };

  const handleManageChannels = async (catalogueId: string) => {
    setSelectedCatalogueId(catalogueId);
    setShowChannelBindingModal(true);
    await loadChannelsData(catalogueId);
  };

  const loadChannelsData = async (catalogueId: string) => {
    try {
      setLoadingChannels(true);
      const [channelsResponse, bindingsResponse] = await Promise.all([
        apiService.getAvailableChannels(),
        apiService.getCatalogueChannelBindings(catalogueId),
      ]);

      if (channelsResponse.statusCode === 1000 && channelsResponse.data) {
        setAvailableChannels(channelsResponse.data || []);
      }

      if (bindingsResponse.statusCode === 1000 && bindingsResponse.data) {
        setChannelBindings(bindingsResponse.data || []);
      }
    } catch (error: any) {
      console.error('Error loading channels data:', error);
      showNotification('error', 'Error', 'Failed to load channels data');
    } finally {
      setLoadingChannels(false);
    }
  };

  const handleBulkBind = async (channels: string[]) => {
    if (!selectedCatalogueId) return;

    try {
      // Get currently bound channels
      const currentChannels = channelBindings.map(b => b.channel);
      
      // Channels to bind (new ones)
      const toBind = channels.filter(ch => !currentChannels.includes(ch));
      
      // Channels to unbind (removed ones)
      const toUnbind = currentChannels.filter(ch => !channels.includes(ch));

      // Bind new channels
      for (const channel of toBind) {
        await apiService.bindCatalogueToChannel(selectedCatalogueId, channel);
      }

      // Unbind removed channels
      for (const channel of toUnbind) {
        await apiService.unbindCatalogueFromChannel(selectedCatalogueId, channel);
      }

      showNotification('success', 'Success', 'Channels updated successfully');
      await loadChannelsData(selectedCatalogueId); // Reload to get updated bindings
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to update channel bindings');
      throw error;
    }
  };

  const confirmDelete = async () => {
    if (!catalogueToDelete) return;

    try {
      setDeleting(true);
      const response = await apiService.deleteCatalogue(catalogueToDelete);
      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'Catalogue deleted successfully');
        setShowDeleteModal(false);
        setCatalogueToDelete(null);
        loadCatalogues(0, true);
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to delete catalogue';
        showNotification('error', 'Error', errorMessage);
        setShowDeleteModal(false);
        setCatalogueToDelete(null);
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to delete catalogue');
      setShowDeleteModal(false);
      setCatalogueToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateCatalogue = () => {
    navigateTo('catalogue-create', undefined, scopedToDataListId ? { listId: scopedToDataListId } : undefined);
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  // Multi-select bulk action — "Assign Products" links chosen product(s) to
  // every selected category at once, via the same bulkLinkProducts
  // primitive Products list's "Add to Category" uses.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedCatalogueIds, setSelectedCatalogueIds] = useState<string[]>([]);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => !prev);
    setSelectedCatalogueIds([]);
  }, []);

  const toggleCatalogueSelection = useCallback((catalogueId: string) => {
    setSelectedCatalogueIds((prev) => (prev.includes(catalogueId) ? prev.filter((id) => id !== catalogueId) : [...prev, catalogueId]));
  }, []);

  const handleBulkAssignProducts = async (productIds: string[]) => {
    setShowProductPicker(false);
    setBulkActionLoading(true);
    try {
      const res = await apiService.bulkLinkProducts({ productIds, addCatalogueIds: selectedCatalogueIds });
      if (res.statusCode === 1000) {
        showNotification('success', 'Assigned', `Product(s) assigned to ${selectedCatalogueIds.length} categor${selectedCatalogueIds.length === 1 ? 'y' : 'ies'}`);
        setSelectionMode(false);
        setSelectedCatalogueIds([]);
        loadCatalogues(0, true);
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to assign products');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to assign products');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const renderContent = () => {
    if (loading && catalogues.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>Loading catalogues...</Text>
        </View>
      );
    }

    if (error && catalogues.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
          <Text style={styles.errorTitle}>Unable to Load Catalogues</Text>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={() => loadCatalogues(0, true)}
          >
            <Text style={styles.retryButtonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <View style={styles.listContainer}>
        <CatalogueList
          catalogues={catalogues}
          loading={false}
          refreshing={refreshing}
          onRefresh={handleRefresh}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
          onCataloguePress={handleCataloguePress}
          onCatalogueEdit={handleCatalogueEdit}
          onCatalogueDelete={handleCatalogueDelete}
          onCatalogueManageChannels={handleManageChannels}
          showActions={!selectionMode}
          emptyMessage="No catalogues yet. Create your first catalogue to get started!"
          selectionMode={selectionMode}
          selectedIds={selectedCatalogueIds}
          onToggleSelect={toggleCatalogueSelection}
        />
      </View>
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
    >
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              <Text style={styles.headerTitle}>{scopedToDataListId ? (scopedListName || 'Catalogues') : 'Catalogues'}</Text>
              {!loading && (
                <Text style={styles.headerSubtitle}>
                  {total} {total === 1 ? 'catalogue' : 'catalogues'}
                </Text>
              )}
            </View>

            <View style={styles.headerActions}>
              <TouchableOpacity
                style={[styles.filterButton, selectionMode && styles.filterButtonActive]}
                onPress={toggleSelectionMode}
                accessibilityLabel={selectionMode ? t('common.cancel') : t('common.select')}
                activeOpacity={0.8}
              >
                <Ionicons name={selectionMode ? 'close' : 'checkbox-outline'} size={20} color={selectionMode ? colors.white : colors.text.secondary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleCreateCatalogue}
                accessibilityLabel={t('common.add')}
                activeOpacity={0.8}
              >
                <Ionicons name="add" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>
          </View>

          {scopedToDataListId && (
            /* Scoped mode — this list is showing only records added through a
               System Data List. Two distinct exits: leaving to the Data Lists
               index vs. staying on Catalogues but dropping the scope filter —
               collapsing them into one button pointed at data-list-records
               caused an exit → immediate bounce-back-in loop, since
               DataListRecordsScreen unconditionally redirects any system list
               straight back into this same scoped screen. */
            <View style={styles.scopedExitRow}>
              <TouchableOpacity
                style={styles.scopedExitBanner}
                onPress={() => navigateTo('data-lists')}
                activeOpacity={0.7}
              >
                <Ionicons name="exit-outline" size={15} color={colors.primary} />
                <Text style={styles.scopedExitBannerText}>Exit to Data Lists</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.scopedExitBanner}
                onPress={() => navigateTo('catalogues', undefined, {})}
                activeOpacity={0.7}
              >
                <Ionicons name="cube-outline" size={15} color={colors.primary} />
                <Text style={styles.scopedExitBannerText}>Exit to Catalogues</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        <ListFilterBar
          controller={list}
          fields={catalogueFields}
          sorts={catalogueSorts}
          onOpenFilters={() => setShowFilters(true)}
          searchPlaceholder={t('catalogues.filter.search')}
          customFieldLabels={Object.fromEntries(customFieldsConfig.fields.map((f) => [f.key, f.label]))}
        />

        {/* Content */}
        {renderContent()}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          visible={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setCatalogueToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Catalogue"
          message="Are you sure you want to delete this catalogue? This action cannot be undone. Make sure the catalogue has no products before deleting."
          confirmText="Delete"
          cancelText="Cancel"
          type="error"
          loading={deleting}
        />

        {/* Channel Binding Modal */}
        <ChannelBindingModal
          visible={showChannelBindingModal}
          onClose={() => {
            setShowChannelBindingModal(false);
            setSelectedCatalogueId(null);
            setAvailableChannels([]);
            setChannelBindings([]);
          }}
          onConfirm={handleBulkBind}
          availableChannels={availableChannels}
          currentBindings={channelBindings}
          catalogueName={catalogues.find(c => (c._id || c.id) === selectedCatalogueId)?.name}
          loading={loadingChannels}
        />

        <FilterSheet
          visible={showFilters}
          title={t('catalogues.filter.title')}
          fields={catalogueFields}
          sorts={catalogueSorts}
          value={list.state}
          customFields={customFieldsConfig}
          onApply={list.apply}
          onClose={() => setShowFilters(false)}
        />

        {selectionMode && selectedCatalogueIds.length > 0 && (
          <View style={styles.bulkActionBar}>
            <Text style={styles.bulkActionCount}>{selectedCatalogueIds.length} selected</Text>
            <TouchableOpacity style={styles.bulkActionButton} onPress={() => setShowProductPicker(true)} disabled={bulkActionLoading}>
              <Ionicons name="cube-outline" size={16} color={colors.white} />
              <Text style={styles.bulkActionButtonText}>Assign Products</Text>
            </TouchableOpacity>
            {bulkActionLoading && <ActivityIndicator size="small" color={colors.white} style={styles.bulkActionLoading} />}
          </View>
        )}

        <ProductSelectorModal
          visible={showProductPicker}
          onClose={() => setShowProductPicker(false)}
          onConfirm={handleBulkAssignProducts}
          title="Assign Products to Categories"
          confirmText="Assign"
          allowMultiple
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  header: {
    backgroundColor: colors.background.surface,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border + '40',
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    flex: 1,
  },
  headerTitle: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
    marginBottom: spacing.xs / 2,
  },
  scopedExitRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.xs,
  },
  scopedExitBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.xs,
  },
  scopedExitBannerText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  addButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: colors.primary,
  },
  filterButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  filterButtonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  bulkActionBar: {
    position: 'absolute',
    left: spacing.lg,
    right: spacing.lg,
    bottom: spacing.lg,
    backgroundColor: colors.textDark,
    borderRadius: designTokens.borderRadius.lg,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bulkActionCount: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
  },
  bulkActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.primary,
  },
  bulkActionButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.xs,
    fontWeight: '600',
  },
  bulkActionLoading: {
    marginLeft: spacing.sm,
  },
  listContainer: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  centerText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  errorTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginTop: spacing.md,
    marginBottom: spacing.xs,
  },
  errorText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  retryButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderRadius: designTokens.borderRadius.md,
  },
  retryButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
  },
});

